export type MessageRenderSegment =
  | { type: 'markdown'; content: string }
  | { type: 'block-math'; content: string }
  | { type: 'inline-math'; content: string };

type DelimiterMatch = {
  type: 'block-math' | 'inline-math';
  start: number;
  end: number;
  latex: string;
};

type TextRange = { start: number; end: number };

function isIndexInRanges(index: number, ranges: TextRange[]): boolean {
  return ranges.some(range => index >= range.start && index < range.end);
}

function addInlineCodeRanges(text: string, from: number, to: number, ranges: TextRange[]) {
  let i = from;
  while (i < to) {
    if (text[i] !== '`') {
      i += 1;
      continue;
    }

    let ticks = 1;
    while (i + ticks < to && text[i + ticks] === '`') ticks += 1;

    const closer = '`'.repeat(ticks);
    const closeAt = text.indexOf(closer, i + ticks);
    if (closeAt === -1 || closeAt >= to) break;

    ranges.push({ start: i, end: closeAt + ticks });
    i = closeAt + ticks;
  }
}

function getCodeProtectedRanges(text: string): TextRange[] {
  const ranges: TextRange[] = [];
  const lines = text.split('\n');
  let offset = 0;
  let fence: { start: number; marker: '`' | '~'; length: number } | null = null;

  for (const line of lines) {
    const lineStart = offset;
    const lineEnd = offset + line.length;
    const fenceOpen = line.match(/^[ \t]{0,3}(`{3,}|~{3,})/);

    if (fence) {
      const fenceClose = line.match(/^[ \t]{0,3}(`{3,}|~{3,})/);
      if (fenceClose && fenceClose[1][0] === fence.marker && fenceClose[1].length >= fence.length) {
        ranges.push({ start: fence.start, end: lineEnd });
        fence = null;
      }
    } else if (fenceOpen) {
      fence = {
        start: lineStart,
        marker: fenceOpen[1][0] as '`' | '~',
        length: fenceOpen[1].length,
      };
    } else {
      addInlineCodeRanges(text, lineStart, lineEnd, ranges);
    }

    offset = lineEnd + 1;
  }

  if (fence) ranges.push({ start: fence.start, end: text.length });
  return ranges;
}

function isProgrammingDollar(text: string, index: number): boolean {
  const previous = text[index - 1];
  const next = text[index + 1];
  const previousIsWord = Boolean(previous && /[A-Za-z0-9_]/.test(previous));
  const nextIsWord = Boolean(next && /[A-Za-z0-9_]/.test(next));

  if (previousIsWord && nextIsWord) return true;
  if (!previousIsWord && next && /[0-9]/.test(next)) return true;
  return false;
}

function tryParseBracketLatex(text: string, openBracket: number): DelimiterMatch | null {
  let i = openBracket + 1;
  while (i < text.length && (text[i] === ' ' || text[i] === '\n' || text[i] === '\t')) i += 1;
  if (text[i] !== '\\') return null;

  const latexStart = i;
  let braceDepth = 0;

  while (i < text.length) {
    const ch = text[i];
    if (ch === '\\') {
      i += 1;
      if (i < text.length) i += 1;
      continue;
    }
    if (ch === '{') braceDepth += 1;
    else if (ch === '}') braceDepth = Math.max(0, braceDepth - 1);
    else if (ch === ']' && braceDepth === 0) {
      return {
        type: 'block-math',
        start: openBracket,
        end: i + 1,
        latex: text.slice(latexStart, i).trim(),
      };
    }
    i += 1;
  }

  return null;
}

function findNextMathDelimiter(
  text: string,
  from: number,
  protectedRanges: TextRange[],
): DelimiterMatch | null {
  let earliest: DelimiterMatch | null = null;

  const consider = (match: DelimiterMatch | null) => {
    if (!match || isIndexInRanges(match.start, protectedRanges)) return;
    if (!earliest || match.start < earliest.start) earliest = match;
  };

  for (let idx = text.indexOf('$$', from); idx !== -1; idx = text.indexOf('$$', idx + 2)) {
    if (isIndexInRanges(idx, protectedRanges)) continue;
    const end = text.indexOf('$$', idx + 2);
    if (end === -1) break;
    consider({
      type: 'block-math',
      start: idx,
      end: end + 2,
      latex: text.slice(idx + 2, end).trim(),
    });
    break;
  }

  for (let idx = text.indexOf('\\[', from); idx !== -1; idx = text.indexOf('\\[', idx + 2)) {
    if (isIndexInRanges(idx, protectedRanges)) continue;
    const end = text.indexOf('\\]', idx + 2);
    if (end === -1) break;
    consider({
      type: 'block-math',
      start: idx,
      end: end + 2,
      latex: text.slice(idx + 2, end).trim(),
    });
    break;
  }

  for (let idx = text.indexOf('\\(', from); idx !== -1; idx = text.indexOf('\\(', idx + 2)) {
    if (isIndexInRanges(idx, protectedRanges)) continue;
    const end = text.indexOf('\\)', idx + 2);
    if (end === -1) break;
    consider({
      type: 'inline-math',
      start: idx,
      end: end + 2,
      latex: text.slice(idx + 2, end).trim(),
    });
    break;
  }

  for (let idx = from; idx < text.length; idx += 1) {
    if (text[idx] !== '$' || text[idx + 1] === '$') continue;
    if (isIndexInRanges(idx, protectedRanges) || isProgrammingDollar(text, idx)) continue;

    let end = idx + 1;
    let foundDollarMath = false;
    while (end < text.length) {
      if (text[end] === '$' && text[end - 1] !== '\\') {
        if (!isIndexInRanges(end, protectedRanges) && !isProgrammingDollar(text, end)) {
          consider({
            type: 'inline-math',
            start: idx,
            end: end + 1,
            latex: text.slice(idx + 1, end).trim(),
          });
          foundDollarMath = true;
        }
        break;
      }
      end += 1;
    }
    if (foundDollarMath) break;
  }

  for (let idx = text.indexOf('[', from); idx !== -1; idx = text.indexOf('[', idx + 1)) {
    if (isIndexInRanges(idx, protectedRanges)) continue;
    consider(tryParseBracketLatex(text, idx));
  }

  return earliest;
}

function stripIncompleteLatex(text: string): string {
  let dollarSearchFrom = 0;
  let open = text.indexOf('$$', dollarSearchFrom);
  while (open !== -1) {
    const close = text.indexOf('$$', open + 2);
    if (close === -1) return text.slice(0, open);
    dollarSearchFrom = close + 2;
    open = text.indexOf('$$', dollarSearchFrom);
  }

  const displayIdx = text.lastIndexOf('\\[');
  if (displayIdx !== -1) {
    const closingIdx = text.indexOf('\\]', displayIdx + 2);
    if (closingIdx === -1) return text.slice(0, displayIdx);
  }

  const inlineIdx = text.lastIndexOf('\\(');
  if (inlineIdx !== -1) {
    const closingIdx = text.indexOf('\\)', inlineIdx + 2);
    if (closingIdx === -1) return text.slice(0, inlineIdx);
  }

  const lastOpenBracket = text.lastIndexOf('[');
  if (lastOpenBracket !== -1) {
    const tail = text.slice(lastOpenBracket);
    if (tryParseBracketLatex(text, lastOpenBracket) === null) {
      let i = 1;
      while (i < tail.length && (tail[i] === ' ' || tail[i] === '\n' || tail[i] === '\t')) i += 1;
      if (tail[i] === '\\') return text.slice(0, lastOpenBracket);
    }
  }

  return text;
}

export function splitMessageRenderSegments(text: string): MessageRenderSegment[] {
  const safeText = stripIncompleteLatex(text);
  const protectedRanges = getCodeProtectedRanges(safeText);
  const segments: MessageRenderSegment[] = [];
  let cursor = 0;

  while (cursor < safeText.length) {
    const match = findNextMathDelimiter(safeText, cursor, protectedRanges);

    if (!match) {
      const remainder = safeText.slice(cursor);
      if (remainder) segments.push({ type: 'markdown', content: remainder });
      break;
    }

    const before = safeText.slice(cursor, match.start);
    if (before) segments.push({ type: 'markdown', content: before });

    if (match.latex) {
      segments.push({ type: match.type, content: match.latex });
    }

    cursor = match.end;
  }

  return segments;
}
